/**
 * MED-938 / E3.5 — composition root for the refreshed two-column participant
 * dashboard. Rendered in place of the legacy dashboard only when the
 * `live_session_delivery` flag is on AND the response carries a `refresh`
 * block. The page-owned scheduling modals stay mounted in ParticipantDashboard;
 * this view receives the same handlers so 1:1/check-in scheduling behaves
 * exactly as before (AC-R2).
 */
import { Box, Container, Grid, Text } from '@mantine/core';
import type { ParticipantDashboardData } from '../../../services/api/participantPortal.js';
import { SessionStartBanner } from '../../../components/participant/SessionStartBanner.js';
import { buildNeedsAttentionItems, formatLongDate } from './dashboardRefreshUtils.js';
import { NeedsAttentionSection } from './NeedsAttentionSection.js';
import { UpcomingSessionsSection } from './UpcomingSessionsSection.js';
import { CompletedSessionsSection } from './CompletedSessionsSection.js';
import { DashboardSidebar } from './DashboardSidebar.js';

interface DashboardRefreshViewProps {
  data: ParticipantDashboardData;
  firstName: string;
  onScheduleCoaching: (coachId: string) => void;
  onScheduleCheckin: (coachId: string, huddleType: 'intro' | 'midpoint_checkin') => void;
}

export function DashboardRefreshView({
  data,
  firstName,
  onScheduleCoaching,
  onScheduleCheckin,
}: DashboardRefreshViewProps) {
  const refresh = data.refresh;
  if (!refresh) return null;

  const needsAttentionItems = buildNeedsAttentionItems(data);

  // MED-1127 — live session for the prominent "Live now" banner (AC6). The
  // refresh payload already carries `is_live` per upcoming row (the same signal
  // the "LIVE NOW" row uses), so no new backend field is needed on the
  // dashboard. cohortId backs the leader "Rejoin session" route (AC10).
  const liveSession = refresh.upcoming_sessions.find(s => s.is_live);
  const cohortId = data.cohort?.id ?? null;

  return (
    <Container size="lg" py="xl" className="med-mobile-container">
      {/* Page header */}
      <Box mb="lg">
        <Text fw={700} size="28px" lh={1.2}>
          Welcome back, {firstName}
        </Text>
        <Text size="sm" c="dimmed" mt={4}>
          {formatLongDate(new Date())}
        </Text>
      </Box>

      {/* Starting-soon banner (waiting-room entry) — unchanged behavior, AC5 */}
      {data.starting_soon && (
        <SessionStartBanner
          state="starting_soon"
          title={data.starting_soon.title}
          isLeading={!!data.starting_soon.is_leading}
          sessionId={data.starting_soon.id}
          cohortId={cohortId}
        />
      )}

      {/* MED-1127 — live banner (AC6/AC10): coexists with the "LIVE NOW" row
          (AC7). Leader gets "Rejoin session" → leader active session; the
          participant gets "Join session" → waiting room. */}
      {liveSession && (
        <SessionStartBanner
          state="live"
          title={liveSession.title}
          isLeading={liveSession.is_leading}
          sessionId={liveSession.id}
          cohortId={cohortId}
        />
      )}

      <Grid gutter="xl">
        {/* Left column — main content */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <NeedsAttentionSection
            items={needsAttentionItems}
            onScheduleCoaching={onScheduleCoaching}
            onScheduleCheckin={onScheduleCheckin}
          />
          <UpcomingSessionsSection sessions={refresh.upcoming_sessions} cohortId={cohortId} />
          <CompletedSessionsSection sessions={refresh.completed_sessions} />
        </Grid.Col>

        {/* Right column — contextual sidebar */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <DashboardSidebar
            program={refresh.program}
            cohortManager={refresh.cohort_manager}
            group={refresh.group}
            modalities={refresh.modalities}
            resources={refresh.resources}
          />
        </Grid.Col>
      </Grid>
    </Container>
  );
}

export default DashboardRefreshView;
